import { useSpendSummaryWithCoverage } from "@/hooks/use-spend-summary";
import useUserInfo from "@/hooks/use-user-info";
import type Widget from "@/model/widget";
import React from "react";
import WidgetCard from "./widget-card";
import { Button } from "@/components/ui/button";
import { CircleArrowRight } from "lucide-react";
import SpendSummarySkeleton from "../spend-summary/spend-summary-skeleton";
import type {
  SpendSummaryAccumulator,
  SpendSummaryNetwork,
} from "@/model/spend-summary";
import SpendSummaryItem from "../spend-summary/spend-summary-item";
import { NoDataAvailable, SomethingWentWrong } from "@/pages/components/empty";
import ScreenRouteNames from "@/constants/screen-route-names";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { useFeatureName } from "@/hooks/use-pageTitle";

type SpendSummaryWidgetProps = {
  className?: string;
  widget: Widget;
};

const SpendSummaryWidget: React.FunctionComponent<SpendSummaryWidgetProps> = ({
  widget,
}) => {
  const { activeCoverage, selectedCoverageYear } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );

  const navigate = useNavigate();
  const userInfo = useUserInfo();
  const { spendQuery } = useSpendSummaryWithCoverage(
    userInfo,
    activeCoverage,
    selectedCoverageYear,
  );

  const viewSpendSummary = () => {
    const targetPath = `/${ScreenRouteNames.SPEND_SUMMARY.path}`;
    navigate(targetPath, {
      replace: false,
    });
  };

  const renderWidget = () => {
    if (spendQuery.isLoading) {
      return <SpendSummarySkeleton />;
    }
    if (!spendQuery.isLoading && spendQuery.data?.data?.length === 0) {
      return <NoDataAvailable />;
    }
    if (spendQuery.isError) {
      return <SomethingWentWrong />;
    }
    const networks: SpendSummaryNetwork[] = spendQuery.data?.data ?? [];
    if (spendQuery && networks.length > 0) {
      const spendSummary: SpendSummaryNetwork[] =
        networks.length > 1
          ? networks.filter((network) => network?.networkType === "IN")
          : networks;

      const memberAccumulators: SpendSummaryAccumulator[] | undefined =
        spendSummary.at(0)?.data?.at(0)?.accumulator;

      const hasSelfAccumOnly = memberAccumulators?.length === 1;
      const hasFamilyAccum = memberAccumulators?.some(
        (accum) => accum.type === "Family",
      );

      let accumulator: SpendSummaryAccumulator | undefined;
      if (hasSelfAccumOnly) {
        accumulator = memberAccumulators?.[0];
      } else if (hasFamilyAccum) {
        accumulator = memberAccumulators?.find(
          (accum) => accum.type === "Family",
        );
      } else {
        accumulator = memberAccumulators?.find(
          (accum) => accum.memberId === userInfo?.memberId,
        );
      }

      return (
        <div className="grid w-full grid-cols-1 items-center justify-between gap-5 motion-translate-y-in-25 motion-duration-[0.5s] @[500px]:grid-cols-2 @[845px]:grid-cols-3 @[1000px]:grid-cols-4 @[1200px]:grid-cols-5">
          <SpendSummaryItem
            planType={spendSummary.at(0)!.PTPlanBenefitType}
            summary={accumulator}
            // Defaults to false when the API omits the config key.
            showFamilyDentalOop={
              spendQuery.data?.config?.showFamilyDentalOop ?? false
            }
            key={accumulator?.memberId}
          />
        </div>
      );
    }
  };

  const footerContent = (
    <Button
      variant="link"
      className="w-full hover:underline"
      onClick={viewSpendSummary}
    >
      View {useFeatureName(ScreenRouteNames.SPEND_SUMMARY.path)}{" "}
      <CircleArrowRight className="ml-1" />
    </Button>
  );

  return (
    <WidgetCard title={widget?.widgetDisplayName} footerContent={footerContent}>
      {renderWidget()}
    </WidgetCard>
  );
};

export default SpendSummaryWidget;
